/**
 * Analytics Summary Cards Component
 *
 * Displays summary metrics in a responsive card grid.
 * Shows total events, category breakdown, severity distribution, and top user.
 */

import { Activity, User, Shield, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import type { AnalyticsSummary } from '@/types/analytics';
import {
  AUDIT_LOG_CATEGORY_LABELS,
  AUDIT_LOG_SEVERITY_LABELS,
  AUDIT_LOG_CATEGORY_COLORS,
  AUDIT_LOG_SEVERITY_COLORS,
  type AuditLogCategory,
  type AuditLogSeverity,
} from '@/types/audit-log';

interface AnalyticsSummaryProps {
  data: AnalyticsSummary;
}

/**
 * Format large numbers with commas
 */
function formatNumber(num: number): string {
  return num.toLocaleString();
}

/**
 * Analytics Summary Cards
 * 4-card grid with key metrics
 */
export function AnalyticsSummary({ data }: AnalyticsSummaryProps) {
  // Get top 5 categories by count
  const topCategories = Object.entries(data.eventsByCategory)
    .filter(([, count]) => count > 0)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5) as [AuditLogCategory, number][];

  // Get severity breakdown
  const severities = Object.entries(data.eventsBySeverity).filter(([, count]) => count > 0) as [
    AuditLogSeverity,
    number,
  ][];

  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      {/* Card 1: Total Events */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Total Events</CardTitle>
          <Activity className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{formatNumber(data.totalEvents)}</div>
          <p className="text-xs text-muted-foreground mt-1">All events in selected period</p>
        </CardContent>
      </Card>

      {/* Card 2: Events by Category */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Top Categories</CardTitle>
          <Shield className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          {topCategories.length > 0 ? (
            <div className="space-y-2">
              {topCategories.map(([category, count]) => (
                <div key={category} className="flex items-center justify-between">
                  <Badge variant="outline" className={AUDIT_LOG_CATEGORY_COLORS[category]}>
                    {AUDIT_LOG_CATEGORY_LABELS[category]}
                  </Badge>
                  <span className="text-sm font-medium">{formatNumber(count)}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">No data</p>
          )}
        </CardContent>
      </Card>

      {/* Card 3: Events by Severity */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">By Severity</CardTitle>
          <AlertTriangle className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          {severities.length > 0 ? (
            <div className="space-y-2">
              {severities.map(([severity, count]) => (
                <div key={severity} className="flex items-center justify-between">
                  <Badge variant="outline" className={AUDIT_LOG_SEVERITY_COLORS[severity]}>
                    {AUDIT_LOG_SEVERITY_LABELS[severity]}
                  </Badge>
                  <span className="text-sm font-medium">{formatNumber(count)}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">No data</p>
          )}
        </CardContent>
      </Card>

      {/* Card 4: Top User */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Most Active User</CardTitle>
          <User className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          {data.topUser ? (
            <div>
              <div className="text-2xl font-bold">{formatNumber(data.topUser.count)}</div>
              <p
                className="text-xs text-muted-foreground mt-1 truncate"
                title={data.topUser.userId}
              >
                {data.topUser.userId}
              </p>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">No user data</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
